/**
 * components/legal/priceList.ts
 *
 * The price list as data (5 Oct 2026): two products, each a monthly price for
 * every property a subscription covers and a monthly price for each of its
 * rooms. It is what the price list states (counsel's Annex 3 at
 * /subscription, `vop/vopPriceList.ts`, and the welcome page's sheet,
 * `priceListCopy.ts` drawn by `pricelist/PriceListPlans.tsx`)
 * and what the admin console names a product by. A mirror of the table the
 * billing engine charges from
 * (`optivo-service/src/services/billing/billingPricing.ts`) —
 * `priceList.parity.test.ts` reads that from the source and fails the moment
 * the two part, because a published price the checkout does not charge is the
 * one thing a price list must never be.
 *
 * OPTIVO Hotels is 2 990 CZK a property and 30 CZK a room; OPTIVO Rentals,
 * for a guesthouse, an apartment, a vacation rental or a campsite, 190 CZK a property and
 * the same 30 CZK a room. The property's type alone decides which, with no
 * size limit on either. What it replaced: the list of 1–3 Oct 2026 (a hotel's
 * fixed fee and 150 CZK a room, a rental by bands of units, two hotels or 21
 * units sent to OPTIVO Portfolio, an offer with no list price), and a flat
 * list of the morning of 5 Oct that was never deployed (2 990 CZK a property,
 * 390 CZK for a rental of one to five units). The bands, the thresholds and
 * the size limit are gone: every account has a list price. The list of 1–3
 * Oct also charged a year paid up front for eleven of its months. Optivo
 * gives no discount since 5 Oct 2026, so the list states each product's
 * prices and nothing off them: a year is twelve months at those prices, and
 * the parity test fails a discount that comes back on either side.
 *
 * The billing page does not read this: it prices from the API, which converts
 * into the subscription's currency and knows the account's own agreements.
 */

export type PriceListPlanKey = "pro" | "rentals";

export type PriceListPlanFamily = "hotels" | "rentals";

export interface PriceListPlan {
    key: PriceListPlanKey;
    family: PriceListPlanFamily;
    /** The monthly price of one covered property, before its rooms, in CZK. */
    propertyMonthlyCzk: number;
    /** The monthly price of each room Optivo prices in it, in CZK. */
    roomMonthlyCzk: number;
    /** Prices go to the PMS up to this many times a day. */
    uploadsPerDay: number;
    /** How many days ahead the prices sent reach. */
    horizonDays: number;
    /** Revenue analyses included in a quarter. */
    analysesPerQuarter: number;
    /** Whether market data comes with the product at no charge. */
    marketDataIncluded: boolean;
}

/** In offer order, as the engine's `BILLING_PLAN_ORDER` states them. */
export const PRICE_LIST_PLANS: readonly PriceListPlan[] = [
    { key: "pro", family: "hotels", propertyMonthlyCzk: 2990, roomMonthlyCzk: 30, uploadsPerDay: 24, horizonDays: 548, analysesPerQuarter: 1, marketDataIncluded: true },
    { key: "rentals", family: "rentals", propertyMonthlyCzk: 190, roomMonthlyCzk: 30, uploadsPerDay: 24, horizonDays: 548, analysesPerQuarter: 1, marketDataIncluded: true },
];

/**
 * How the price list names each product. The keys are the engine's and are
 * older than the names: `pro` is what OPTIVO Hotels was stored as while it was
 * a plan a subscription chose (and, before 3 Oct 2026, called Optivo Pro).
 * Since 5 Oct 2026 a key names the product a property is priced on, by what
 * the property is; nobody chooses one.
 */
export const PRICE_LIST_PLAN_NAMES: Record<PriceListPlanKey, string> = {
    pro: "OPTIVO Hotels",
    rentals: "OPTIVO Rentals",
};

/**
 * The property types OPTIVO Rentals is for — the Property Profile answers
 * that name a guesthouse, an apartment, a vacation rental or a campsite, in the order the
 * list names them. Such a property is OPTIVO Rentals whatever its size; every
 * other property is OPTIVO Hotels.
 */
export const RENTALS_PROPERTY_TYPES = ["pension_bb", "apartment", "villa_vacation_rental", "campsite"] as const;

/**
 * The fixed rate the euro guide is worked out at, in CZK per euro. The public
 * price list shows a euro figure beside each price for a reader who thinks in
 * euros, and says it is a guide: billing is CZK-only, nothing is charged in
 * euros, and the engine knows nothing of this. A fixed rate rather than the
 * day's, so the page states the same figure tomorrow.
 */
export const EUR_GUIDE_CZK_PER_EUR = 25;

/**
 * A CZK amount as the guide states it, in euros at the fixed rate: whole
 * euros from ten up, and to the cent below that. A whole euro would state a
 * small price up to half a euro out — 30 CZK a room is 1.20 €, which "1 €"
 * puts a sixth short — while on a price of a hundred euros the cents are
 * noise a guide is better without.
 */
export function eurGuide(czk: number): number {
    const euros = czk / EUR_GUIDE_CZK_PER_EUR;
    return euros >= 10 ? Math.round(euros) : Math.round(euros * 100) / 100;
}

/** A product by its key, or undefined for a key the price list does not sell. */
export function priceListPlan(key: string | null | undefined): PriceListPlan | undefined {
    return PRICE_LIST_PLANS.find((plan) => plan.key === key);
}

/**
 * "1 000 CZK" — thousands grouped by a plain space, the way the legal pages
 * have always written amounts, in both languages. A plain space rather than a
 * no-break one: the documents are plain strings, and the text is read by
 * tests and people alike.
 */
export function formatPriceListCzk(value: number): string {
    return `${String(value).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} CZK`;
}
